import React, { useState, useEffect, useMemo } from 'react';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import toast from 'react-hot-toast';
import Drawer from '../../Drawer';
import { createCampaignRequest } from '~/services/campaign';
import { apiUrl, authHeaders } from '~/services/auth';
import { createMedicalCenter } from '~/services/medical-centers';
import { useNavigate } from 'react-router-dom';
import { GENDER_OPTIONS } from '~/constants/metadata';
import { requiredStyles } from './styles';
import ReferringProfessionalField, {
    EMPTY_REFERRING_PROFESSIONAL,
    REFERRING_PROFESSIONAL_REFERRAL_CODES,
    ReferringProfessional,
    isReferringProfessionalMissing,
    referringProfessionalPayload,
} from '../ReferringProfessionalField';

// Referral sources ("Referred By") for which the Medical Center picker is
// shown and its selection is saved. Matched on ReferredBySource.code.
const MEDICAL_CENTER_REFERRAL_CODES = [
    'med_center',
    'case_manager',
    'financial',
    'medical_professional',
    'social_worker',
    'tx_coord',
];

// Dev-only random form data
const MOVIE_CHARACTERS = [
    { first: 'Neo', last: 'Anderson', nick: 'The One' },
    { first: 'Trinity', last: 'Unknown', nick: 'Trin' },
    { first: 'Morpheus', last: 'Zion', nick: 'Captain' },
    { first: 'Agent', last: 'Smith', nick: 'Mr. Smith' },
    { first: 'Niobe', last: 'Zion', nick: '' },
    { first: 'Tank', last: 'Operator', nick: 'Tanky' },
    { first: 'Cypher', last: 'Reagan', nick: '' },
    { first: 'Mouse', last: 'Crew', nick: '' },
    { first: 'Dozer', last: 'Operator', nick: '' },
    { first: 'Switch', last: 'Crew', nick: '' },
    { first: 'Link', last: 'Operator', nick: '' },
    { first: 'Sati', last: 'Program', nick: '' },
    { first: 'Seraph', last: 'Guardian', nick: '' },
    { first: 'Merovingian', last: 'Exile', nick: 'Merv' },
    { first: 'Persephone', last: 'Exile', nick: '' },
    { first: 'Apoc', last: 'Crew', nick: '' },
    { first: 'Lock', last: 'Commander', nick: '' },
    { first: 'Ghost', last: 'Captain', nick: '' },
    { first: 'Rama', last: 'Kandra', nick: '' },
    { first: 'Keymaker', last: 'Program', nick: 'Keys' },
    { first: 'Luke', last: 'Skywalker', nick: '' },
    { first: 'Leia', last: 'Organa', nick: 'Princess' },
    { first: 'Han', last: 'Solo', nick: 'Scruffy' },
    { first: 'Frodo', last: 'Baggins', nick: '' },
    { first: 'Gandalf', last: 'Grey', nick: 'Mithrandir' },
    { first: 'Aragorn', last: 'Elessar', nick: 'Strider' },
    { first: 'Ellen', last: 'Ripley', nick: '' },
    { first: 'Sarah', last: 'Connor', nick: '' },
    { first: 'Marty', last: 'McFly', nick: '' },
    { first: 'Doc', last: 'Brown', nick: '' },
    { first: 'Leonardo', last: 'da Vinci', nick: 'Leo' },
    { first: 'Michelangelo', last: 'Buonarroti', nick: '' },
    { first: 'Vincent', last: 'van Gogh', nick: '' },
    { first: 'Pablo', last: 'Picasso', nick: '' },
    { first: 'Claude', last: 'Monet', nick: '' },
    { first: 'Frida', last: 'Kahlo', nick: '' },
    { first: 'Rembrandt', last: 'van Rijn', nick: '' },
    { first: 'Salvador', last: 'Dali', nick: '' },
    { first: 'Georgia', last: "O'Keeffe", nick: '' },
    { first: 'Andy', last: 'Warhol', nick: '' },
    { first: 'Artemisia', last: 'Gentileschi', nick: '' },
    { first: 'Gustav', last: 'Klimt', nick: '' },
    { first: 'Edvard', last: 'Munch', nick: '' },
    { first: 'Henri', last: 'Matisse', nick: '' },
    { first: 'Caravaggio', last: 'Merisi', nick: '' },
    { first: 'Sandro', last: 'Botticelli', nick: '' },
    { first: 'Jackson', last: 'Pollock', nick: '' },
    { first: 'Mary', last: 'Cassatt', nick: '' },
    { first: 'Katsushika', last: 'Hokusai', nick: '' },
    { first: 'Yayoi', last: 'Kusama', nick: '' },
];

const CITIES = [
    'Portland',
    'Austin',
    'Denver',
    'Seattle',
    'Boston',
    'Chicago',
    'Nashville',
    'Asheville',
    'Savannah',
    'Boise',
    'Burlington',
    'Santa Fe',
    'Raleigh',
    'Tampa',
    'Bend',
];

const STREETS = [
    '123 Main St',
    '456 Oak Ave',
    '789 Elm Blvd',
    '321 Pine Rd',
    '654 Maple Dr',
    '987 Cedar Ln',
    '111 Birch Way',
    '222 Walnut Ct',
    '333 Spruce Pl',
    '444 Willow Ter',
];

const MIDDLE_NAMES = [
    '',
    'James',
    'Marie',
    'Ray',
    'Lee',
    'Anne',
    'Jo',
    'Thomas',
];

const pick = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

const randomPhone = () => {
    const d = () => Math.floor(Math.random() * 10);
    return `(${d()}${d()}${d()}) ${d()}${d()}${d()}-${d()}${d()}${d()}${d()}`;
};

const randomZip = () => String(Math.floor(10000 + Math.random() * 90000));

const randomDate = () => {
    const year = 1960 + Math.floor(Math.random() * 45);
    const month = String(1 + Math.floor(Math.random() * 12)).padStart(2, '0');
    const day = String(1 + Math.floor(Math.random() * 28)).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

interface SelectOption {
    value: number | string;
    label: string;
}

const US_STATES: Record<string, string> = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District Of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
};

// A medical center's location is a state's full name or 'Treatment Abroad'.
const medCenterStateOptions = [
    ...Object.entries(US_STATES).map(([value, label]) => ({
        value,
        label,
    })),
    { value: 'XX', label: 'Treatment Abroad' },
];

const searchMedicalCenters = async (state: string) => {
    const headers = authHeaders();
    const url = `${apiUrl}/v2/searchMedicalCenters`;
    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ state }),
    });
    if (!resp.ok) throw new Error('Failed to search medical centers');
    return await resp.json();
};

interface Props {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    metadata: any;
    states: any[];
    onSuccess?: () => void;
}

const sectionStyles =
    'mb-[16px] mt-[24px] text-[22px]/[28px] font-medium uppercase text-[#949599] border-b border-stroke pb-2';
const labelStyles =
    'mb-[8px] block text-[14px]/[18px] font-medium text-[#131212]';
const inputStyles =
    'rounded-lg border border-stroke px-[12px] py-[8px] text-[14px]/[18px] text-[#131212] focus:border-primary focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500';
const errorTextClass = 'mt-1 text-sm text-[#ff4b4b]';

const AddCampaignRequestDrawer: React.FC<Props> = ({
    isOpen,
    setIsOpen,
    metadata,
    states,
    onSuccess,
}) => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);
    const [contactError, setContactError] = useState<string | null>(null);

    const methods = useForm({
        defaultValues: {
            // Initial Contact
            initial_first_name: '',
            initial_last_name: '',
            initial_email: '',
            initial_phone: '',
            request_source: '',
            campaign_is_for: null as any,
            heard_of_source: null as any,
            heard_of_source_other: '',

            // Community
            community_types: [] as any[],

            // Primary Contact
            primary_first_name: '',
            primary_middle_name: '',
            primary_last_name: '',
            primary_email: '',
            primary_phone: '',
            primary_address_1: '',
            primary_address_2: '',
            primary_city: '',
            primary_state: null as any,
            primary_zip_code: '',
            primary_relationship_to_client: null as any,
            primary_is_emergency_contact: false,

            // Client Information
            patient_first_name: '',
            patient_last_name: '',
            patient_middle_name: '',
            patient_nicknames: '',
            patient_email: '',
            patient_phone1: '',
            patient_address_1: '',
            patient_address_2: '',
            patient_city: '',
            patient_state: null as any,
            patient_zip_code: '',
            patient_birth_date: '',
            patient_gender: '',

            // Primary Medical Reason
            medical_reason: null as any,
            primary_medical_issue: null as any,
            injury_time_frame: null as any,
            medical_issue_date: '',
            medical_issue_notes: '',

            // Secondary Medical Reason
            secondary_medical_issue: null as any,

            // Insurance
            insurance_type: null as any,

            // Notes
            notes: '',
        },
    });

    const { reset, handleSubmit, control, formState, watch, setValue } =
        methods;
    const { errors } = formState;

    const selectedMedicalReason = watch('medical_reason');
    const selectedCampaignIsFor = watch('campaign_is_for');
    const selectedHeardOfSource = watch('heard_of_source');
    const primaryRelationshipToClient = watch('primary_relationship_to_client');

    const [secondaryMedicalReason, setSecondaryMedicalReason] = useState<
        number | null
    >(null);
    const [primarySameAs, setPrimarySameAs] = useState<
        'initial_contact' | 'client' | null
    >(null);
    const prevPrimarySameAsRef = React.useRef<
        'initial_contact' | 'client' | null
    >(null);

    // Watch for changes to determine if we should sync
    const initialFirstName = watch('initial_first_name');
    const initialLastName = watch('initial_last_name');
    const initialEmail = watch('initial_email');
    const initialPhone = watch('initial_phone');

    const patientFirstName = watch('patient_first_name');
    const patientMiddleName = watch('patient_middle_name');
    const patientLastName = watch('patient_last_name');
    const patientEmail = watch('patient_email');
    const patientPhone1 = watch('patient_phone1');
    const patientAddress1 = watch('patient_address_1');
    const patientAddress2 = watch('patient_address_2');
    const patientCity = watch('patient_city');
    const patientState = watch('patient_state');
    const patientZip = watch('patient_zip_code');

    // Transform metadata to options
    const requestSourceOptions = useMemo(
        () =>
            (metadata?.request_sources || []).map((option: any) => ({
                value: option?.value,
                label: option?.label,
            })),
        [metadata],
    );

    const selfRelationshipOption = useMemo(() => {
        const self = (metadata?.campaign_is_for || []).find(
            (item: any) => item?.code === 'self',
        );
        return self ? { value: self.id, label: self.name } : null;
    }, [metadata]);

    const relationshipOptions = useMemo(
        () =>
            (metadata?.campaign_is_for || [])
                .filter(
                    (item: any) => typeof item === 'object' && item !== null,
                )
                .map((item: any) => ({ value: item?.id, label: item?.name })),
        [metadata],
    );

    const heardOfOptions = useMemo(
        () =>
            (metadata?.heard_of_sources || []).map((item: any) => ({
                value: item?.id,
                label: item?.name,
                code: item?.code,
            })),
        [metadata],
    );

    const communityOptions = useMemo(
        () =>
            (metadata?.community_types || []).map((item: any) => ({
                value: item?.id,
                label: item?.name,
            })),
        [metadata],
    );

    const stateOptions = useMemo(
        () =>
            (states || []).map((state: any) => ({
                value: state?.id,
                label: state?.state,
            })),
        [states],
    );

    const medicalReasonOptions = useMemo(
        () =>
            (metadata?.medical_reasons || []).map((reason: any) => ({
                value: reason?.id,
                label: reason?.name,
            })),
        [metadata],
    );

    const medicalIssueOptions = useMemo(
        () =>
            (metadata?.medical_issues || [])
                .filter((issue: any) => {
                    if (!selectedMedicalReason?.value) return true;
                    const issueReason =
                        typeof issue?.medical_reason === 'object'
                            ? issue?.medical_reason?.id
                            : issue?.medical_reason;
                    return issueReason === selectedMedicalReason.value;
                })
                .map((issue: any) => ({
                    value: issue?.id,
                    label: issue?.name,
                })),
        [metadata, selectedMedicalReason],
    );

    const injuryTimeFrameOptions = useMemo(
        () =>
            (metadata?.injury_time_frames || [])
                .filter((frame: any) => {
                    if (!selectedMedicalReason?.value) return true;
                    const frameReason =
                        typeof frame?.medical_reason === 'object'
                            ? frame?.medical_reason?.id
                            : frame?.medical_reason;
                    return frameReason === selectedMedicalReason.value;
                })
                .map((frame: any) => ({
                    value: frame?.id,
                    label: frame?.name,
                })),
        [metadata, selectedMedicalReason],
    );

    const secondaryMedicalIssueOptions = useMemo(() => {
        if (!secondaryMedicalReason) return [];
        return (metadata?.medical_issues || [])
            .filter((issue: any) => {
                const issueReason =
                    typeof issue?.medical_reason === 'object'
                        ? issue?.medical_reason?.id
                        : issue?.medical_reason;
                return issueReason === secondaryMedicalReason;
            })
            .map((issue: any) => ({
                value: issue?.id,
                label: issue?.name,
            }));
    }, [metadata, secondaryMedicalReason]);

    const insuranceOptions = useMemo(
        () =>
            (metadata?.insurance_types || []).map((type: any) => ({
                value: type?.id,
                label: type?.name,
            })),
        [metadata],
    );

    // Check if the selected relationship is "self"
    const isCampaignForSelf = useMemo(() => {
        const selfOption = (metadata?.campaign_is_for || []).find(
            (item: any) => item?.code === 'self',
        );
        return selfOption && selectedCampaignIsFor?.value === selfOption.id;
    }, [metadata, selectedCampaignIsFor]);

    // Primary contact is locked (same as client) when "Same as" is set or relationship is "self"
    const isPrimaryContactSelf = useMemo(
        () =>
            selfRelationshipOption != null &&
            primaryRelationshipToClient?.value === selfRelationshipOption.value,
        [selfRelationshipOption, primaryRelationshipToClient],
    );
    const primaryFieldsLocked = primarySameAs !== null || isPrimaryContactSelf;

    // Check if the selected "Referred By" is "Other"
    const isHeardOfSourceOther = useMemo(() => {
        const otherOption = (metadata?.heard_of_sources || []).find(
            (item: any) => item?.code === 'other_prof',
        );
        return otherOption && selectedHeardOfSource?.value === otherOption.id;
    }, [metadata, selectedHeardOfSource]);

    // Whether the selected "Referred By" source should show the Medical Center picker
    const isMedCenterSelected = MEDICAL_CENTER_REFERRAL_CODES.includes(
        selectedHeardOfSource?.code ?? '',
    );

    const fillRandomData = () => {
        const contact = pick(MOVIE_CHARACTERS);
        const patient = pick(MOVIE_CHARACTERS.filter((c) => c !== contact));
        const primaryContact = pick(
            MOVIE_CHARACTERS.filter((c) => c !== contact && c !== patient),
        );
        const ts = Date.now().toString(36);

        const pickOption = <T extends { value: any; label: string }>(
            opts: T[],
        ): T | null => (opts.length ? pick(opts) : null);

        // Initial Contact
        setValue('initial_first_name', contact.first);
        setValue('initial_last_name', contact.last);
        setValue(
            'initial_email',
            `alex+${contact.first.toLowerCase()}${ts}@bowst.com`,
        );
        setValue('initial_phone', randomPhone());
        setValue(
            'request_source',
            pickOption(requestSourceOptions)?.value || '',
        );
        setValue('campaign_is_for', pickOption(relationshipOptions));
        const heardOf = pickOption(heardOfOptions);
        setValue('heard_of_source', heardOf);
        if ((heardOf as any)?.code === 'other_prof') {
            setValue('heard_of_source_other', 'The Oracle told me');
        }

        // Client
        setValue('patient_first_name', patient.first);
        setValue('patient_last_name', patient.last);
        setValue('patient_middle_name', pick(MIDDLE_NAMES));
        setValue('patient_nicknames', patient.nick);
        setValue(
            'patient_email',
            `alex+${patient.first.toLowerCase()}${ts}@bowst.com`,
        );
        setValue('patient_phone1', randomPhone());
        setValue('patient_address_1', pick(STREETS));
        setValue(
            'patient_address_2',
            Math.random() > 0.5
                ? `Apt ${Math.floor(Math.random() * 300) + 1}`
                : '',
        );
        setValue('patient_city', pick(CITIES));
        setValue('patient_state', pickOption(stateOptions));
        setValue('patient_zip_code', randomZip());
        setValue('patient_birth_date', randomDate());
        setValue('patient_gender', pick(['Male', 'Female', 'Nonbinary']));

        // Primary Contact
        setValue('primary_first_name', primaryContact.first);
        setValue('primary_middle_name', pick(MIDDLE_NAMES));
        setValue('primary_last_name', primaryContact.last);
        setValue(
            'primary_email',
            `alex+${primaryContact.first.toLowerCase()}${ts}@bowst.com`,
        );
        setValue('primary_phone', randomPhone());
        setValue('primary_address_1', pick(STREETS));
        setValue('primary_address_2', '');
        setValue('primary_city', pick(CITIES));
        setValue('primary_state', pickOption(stateOptions));
        setValue('primary_zip_code', randomZip());
        setValue(
            'primary_relationship_to_client',
            pickOption(relationshipOptions),
        );
        setValue('primary_is_emergency_contact', Math.random() > 0.5);

        // Medical
        const medReason = pickOption(medicalReasonOptions);
        setValue('medical_reason', medReason);
        setValue('medical_issue_date', randomDate());
        setValue(
            'medical_issue_notes',
            `Unplugged from the Matrix on ${new Date().toLocaleDateString()}`,
        );

        // Insurance
        setValue('insurance_type', pickOption(insuranceOptions));

        // Notes
        const quotes = [
            'There is no spoon.',
            'I know kung fu.',
            'Welcome to the real world.',
            'The Matrix has you.',
            'Free your mind.',
            'What is the Matrix?',
            'Dodge this.',
            'Not like this... not like this.',
            'Everything that has a beginning has an end.',
            'I can only show you the door. You have to walk through it.',
        ];
        setValue('notes', pick(quotes));

        setPrimarySameAs(null);
    };

    // Medical center selection state
    const [selectedMedCenterState, setSelectedMedCenterState] =
        useState<SelectOption | null>(null);
    const [selectedMedicalCenter, setSelectedMedicalCenter] =
        useState<SelectOption | null>(null);
    const [medicalCenterOptions, setMedicalCenterOptions] = useState<
        SelectOption[]
    >([]);
    const [loadingCenters, setLoadingCenters] = useState(false);
    const [showAddCenterForm, setShowAddCenterForm] = useState(false);
    const [newCenterName, setNewCenterName] = useState('');
    const [isCreatingCenter, setIsCreatingCenter] = useState(false);
    const [referringProfessional, setReferringProfessional] =
        useState<ReferringProfessional>(EMPTY_REFERRING_PROFESSIONAL);

    const isMedCenterMissing = isMedCenterSelected && !selectedMedicalCenter;
    const showMedCenterError = formState.isSubmitted && isMedCenterMissing;
    const isReferringSourceSelected =
        REFERRING_PROFESSIONAL_REFERRAL_CODES.includes(
            selectedHeardOfSource?.code ?? '',
        );
    const isReferrerMissing =
        isReferringSourceSelected &&
        isReferringProfessionalMissing(referringProfessional);
    const showReferrerError = formState.isSubmitted && isReferrerMissing;

    // A picked person belongs to the old center; a typed name doesn't
    const clearPickedProfessional = () =>
        setReferringProfessional((prev) =>
            prev.isNew ? prev : EMPTY_REFERRING_PROFESSIONAL,
        );

    const loadMedicalCenters = async (state: string) => {
        setLoadingCenters(true);
        try {
            const centers = await searchMedicalCenters(state);
            const opts: SelectOption[] = centers.map((center: any) => ({
                label: center.name,
                value: center.id,
            }));
            setMedicalCenterOptions(opts);
        } catch (error) {
            console.error('Error loading medical centers:', error);
        } finally {
            setLoadingCenters(false);
        }
    };

    const handleMedCenterStateChange = (option: SelectOption | null) => {
        setSelectedMedCenterState(option);
        setSelectedMedicalCenter(null);
        clearPickedProfessional();
        setMedicalCenterOptions([]);
        setShowAddCenterForm(false);
        setNewCenterName('');

        if (option) {
            loadMedicalCenters(option.label);
        }
    };

    const handleMedicalCenterChange = (option: SelectOption | null) => {
        if (option?.value !== selectedMedicalCenter?.value) {
            clearPickedProfessional();
        }
        setSelectedMedicalCenter(option);
    };

    const handleAddCenter = async () => {
        if (!newCenterName.trim() || !selectedMedCenterState) return;

        setIsCreatingCenter(true);
        try {
            const center = await createMedicalCenter({
                name: newCenterName.trim(),
                location: selectedMedCenterState.label,
            });
            const newOption: SelectOption = {
                value: center.id,
                label: center.name,
            };
            setMedicalCenterOptions((prev) => [...prev, newOption]);
            setSelectedMedicalCenter(newOption);
            clearPickedProfessional();
            setShowAddCenterForm(false);
            setNewCenterName('');
        } catch (error) {
            console.error('Error creating medical center:', error);
        } finally {
            setIsCreatingCenter(false);
        }
    };

    // Reset medical center state when heard_of_source changes away from med_center
    useEffect(() => {
        if (!isMedCenterSelected) {
            setSelectedMedCenterState(null);
            setSelectedMedicalCenter(null);
            setMedicalCenterOptions([]);
            setShowAddCenterForm(false);
            setNewCenterName('');
            setReferringProfessional(EMPTY_REFERRING_PROFESSIONAL);
        }
    }, [isMedCenterSelected]);

    // Clear medical issue and injury timeframe when medical reason changes
    React.useEffect(() => {
        setValue('primary_medical_issue', null);
        setValue('injury_time_frame', null);
    }, [selectedMedicalReason, setValue]);

    // Reset "Same as" toggle when relationship changes
    React.useEffect(() => {
        setPrimarySameAs(null);
    }, [isCampaignForSelf]);

    // Sync primary contact fields from selected source when toggled on or when primary is "self"
    React.useEffect(() => {
        if (primarySameAs === 'client' || isPrimaryContactSelf) {
            setValue('primary_first_name', patientFirstName || '');
            setValue('primary_middle_name', patientMiddleName || '');
            setValue('primary_last_name', patientLastName || '');
            setValue('primary_email', patientEmail || '');
            setValue('primary_phone', patientPhone1 || '');
            setValue('primary_address_1', patientAddress1 || '');
            setValue('primary_address_2', patientAddress2 || '');
            setValue('primary_city', patientCity || '');
            setValue('primary_state', patientState || null);
            setValue('primary_zip_code', patientZip || '');
            if (selfRelationshipOption) {
                setValue(
                    'primary_relationship_to_client',
                    selfRelationshipOption,
                );
            }
        } else if (primarySameAs === 'initial_contact') {
            setValue('primary_first_name', initialFirstName || '');
            setValue('primary_last_name', initialLastName || '');
            setValue('primary_email', initialEmail || '');
            setValue('primary_phone', initialPhone || '');
            if (selectedCampaignIsFor) {
                setValue(
                    'primary_relationship_to_client',
                    selectedCampaignIsFor,
                );
            }
        } else if (
            primarySameAs === null &&
            prevPrimarySameAsRef.current !== null
        ) {
            // User toggled off "Same as Client" or "Same as Initial Contact" — clear relationship so they can edit
            setValue('primary_relationship_to_client', null);
        }
        prevPrimarySameAsRef.current = primarySameAs;
    }, [
        primarySameAs,
        isPrimaryContactSelf,
        patientFirstName,
        patientMiddleName,
        patientLastName,
        patientEmail,
        patientPhone1,
        patientAddress1,
        patientAddress2,
        patientCity,
        patientState,
        patientZip,
        initialFirstName,
        initialLastName,
        initialEmail,
        initialPhone,
        setValue,
        selfRelationshipOption,
        selectedCampaignIsFor,
    ]);

    // Sync Initial Contact to Client Info and set Primary Contact relationship when "self"
    React.useEffect(() => {
        if (isCampaignForSelf) {
            setValue('patient_first_name', initialFirstName);
            setValue('patient_last_name', initialLastName);
            setValue('patient_email', initialEmail);
            setValue('patient_phone1', initialPhone);
            if (selfRelationshipOption) {
                setValue(
                    'primary_relationship_to_client',
                    selfRelationshipOption,
                );
            }
        }
    }, [
        isCampaignForSelf,
        initialFirstName,
        initialLastName,
        initialEmail,
        initialPhone,
        setValue,
        selfRelationshipOption,
    ]);

    const onSubmit = async (values: any) => {
        setIsSubmitting(true);
        setServerError(null);
        setContactError(null);

        // Validate that at least one contact method is provided (phone or email)
        const hasEmail = values.initial_email && values.initial_email.trim();
        const hasPhone = values.initial_phone && values.initial_phone.trim();

        if (!hasEmail && !hasPhone) {
            setContactError('Please provide at least an email or phone number');
            setIsSubmitting(false);
            return;
        }

        if (isMedCenterMissing) {
            toast.error('Please select the referring Medical Center');
            setIsSubmitting(false);
            return;
        }

        if (isReferrerMissing) {
            toast.error('Please add the referring Medical Professional');
            setIsSubmitting(false);
            return;
        }

        // Define payload outside try block so it's accessible in catch
        const payload: any = {
            // Initial Contact
            initial_first_name: values.initial_first_name,
            initial_last_name: values.initial_last_name,
            initial_email: values.initial_email,
            initial_phone: values.initial_phone,
            request_source: values.request_source || 'admin',
            campaign_is_for_id: values.campaign_is_for?.value ?? null,
            heard_of_source_id: values.heard_of_source?.value ?? null,
            heard_of_source_other: values.heard_of_source_other || '',

            // Community
            community_types: (values.community_types || []).map(
                (ct: any) => ct.value,
            ),

            // Primary Contact
            primary_first_name: values.primary_first_name || '',
            primary_middle_name: values.primary_middle_name || '',
            primary_last_name: values.primary_last_name || '',
            primary_email: values.primary_email || '',
            primary_phone: values.primary_phone || '',
            primary_address_1: values.primary_address_1 || '',
            primary_address_2: values.primary_address_2 || '',
            primary_city: values.primary_city || '',
            primary_state_id: values.primary_state?.value ?? null,
            primary_zip_code: values.primary_zip_code || '',
            primary_relationship_to_client_id:
                values.primary_relationship_to_client?.value ?? null,
            primary_is_emergency_contact:
                values.primary_is_emergency_contact || false,

            // Client Information
            patient_first_name: values.patient_first_name,
            patient_last_name: values.patient_last_name,
            patient_middle_name: values.patient_middle_name || '',
            patient_nicknames: values.patient_nicknames || '',
            patient_email: values.patient_email || '',
            patient_phone1: values.patient_phone1 || '',
            patient_phone1_type: 'mobile',
            patient_address_1: values.patient_address_1 || '',
            patient_address_2: values.patient_address_2 || '',
            patient_city: values.patient_city || '',
            patient_state_id: values.patient_state?.value ?? null,
            patient_zip_code: values.patient_zip_code || '',
            patient_birth_date: values.patient_birth_date || null,
            patient_gender: values.patient_gender || '',
            patient_marital_status: '',

            // Primary Medical Reason
            medical_reason_id: values.medical_reason?.value ?? null,
            primary_medical_issue_id:
                values.primary_medical_issue?.value ?? null,
            injury_time_frame_id: values.injury_time_frame?.value ?? null,
            medical_issue_date: values.medical_issue_date || null,
            medical_issue_notes: values.medical_issue_notes || '',

            // Secondary Medical Reason
            secondary_medical_issue_id:
                values.secondary_medical_issue?.value ?? null,

            // Insurance
            insurance_type_id: values.insurance_type?.value ?? null,

            // Notes
            notes: values.notes || '',
        };

        // Include medical center if referred by a medical-center-related source
        if (
            MEDICAL_CENTER_REFERRAL_CODES.includes(
                values.heard_of_source?.code ?? '',
            )
        ) {
            payload.medical_center_id = selectedMedicalCenter?.value ?? null;
        }
        if (
            REFERRING_PROFESSIONAL_REFERRAL_CODES.includes(
                values.heard_of_source?.code ?? '',
            )
        ) {
            Object.assign(
                payload,
                referringProfessionalPayload(referringProfessional),
            );
        }

        try {
            const response = await createCampaignRequest(payload);

            if (response.success) {
                toast.success('Campaign request created successfully');
                reset();
                setIsOpen(false);
                if (onSuccess) {
                    onSuccess();
                }
                // Navigate to the newly created campaign request
                if (response.campaign_request?.id) {
                    navigate(
                        `/campaign-requests/${response.campaign_request.id}`,
                    );
                }
            }
        } catch (error: any) {
            console.error('Error creating campaign request:', error);

            let errorMessage =
                error.message || 'Failed to create campaign request';
            let toastMessage = errorMessage;

            if (error.response?.data?.detail) {
                toastMessage = error.response.data.detail;
            }

            if (error.response?.data?.details) {
                errorMessage +=
                    '\n\nDetails:\n' + error.response.data.details.join('\n');
                toastMessage = error.response.data.details.join('\n');
            }

            if (error.response?.data?.traceback) {
                console.error(
                    'Server traceback:',
                    error.response.data.traceback,
                );
            }

            toast.error(toastMessage, {
                duration: 6000,
                style: {
                    maxWidth: '500px',
                },
            });

            setServerError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            reset();
            setServerError(null);
            setContactError(null);
            setIsOpen(false);
        }
    };

    return (
        <Drawer
            isOpen={isOpen}
            setIsOpen={handleClose}
            title='Add Campaign Request'
        >
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    {serverError && (
                        <div className='bg-red-50 mb-4 rounded-lg border border-red p-4 text-sm text-red'>
                            <strong>Error:</strong>
                            <pre className='font-sans mt-2 whitespace-pre-wrap'>
                                {serverError}
                            </pre>
                        </div>
                    )}

                    {import.meta.env.VITE_TEST_MODE === 'true' && (
                        <button
                            type='button'
                            onClick={fillRandomData}
                            className='mb-4 rounded-full border-2 border-dashed border-[#949599] px-4 py-1.5 text-[12px]/[16px] font-bold uppercase text-[#949599] transition-colors hover:border-primary hover:text-primary'
                        >
                            Fill Random Data
                        </button>
                    )}

                    {/* INITIAL CONTACT */}
                    <h2 className={sectionStyles}>INITIAL CONTACT</h2>

                    <div className='mb-5'>
                        <label className={`w-full ${labelStyles}`}>
                            Source
                        </label>
                        <Controller
                            name='request_source'
                            control={control}
                            render={({ field }) => (
                                <div className='flex flex-wrap gap-2'>
                                    {requestSourceOptions.map(
                                        (option: {
                                            value: string;
                                            label: string;
                                        }) => (
                                            <button
                                                key={option.value}
                                                type='button'
                                                onClick={() =>
                                                    field.onChange(
                                                        field.value ===
                                                            option.value
                                                            ? ''
                                                            : option.value,
                                                    )
                                                }
                                                className={`rounded-full border-2 px-5 py-2 text-[14px]/[18px] font-bold uppercase transition-colors ${
                                                    field.value === option.value
                                                        ? 'border-[#2D2E33] bg-[#2D2E33] text-white'
                                                        : 'border-[#2D2E33] bg-white text-[#2D2E33] hover:bg-gray-50'
                                                }`}
                                            >
                                                {option.label}
                                            </button>
                                        ),
                                    )}
                                </div>
                            )}
                        />
                    </div>

                    <div className='mb-5 flex gap-3'>
                        <div className='w-1/2'>
                            <label
                                htmlFor='add-initial-first-name'
                                className={`w-full ${labelStyles}`}
                            >
                                First Name
                            </label>
                            <Controller
                                name='initial_first_name'
                                control={control}
                                render={({ field }) => (
                                    <input
                                        id='add-initial-first-name'
                                        className={`w-full ${inputStyles}`}
                                        type='text'
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                        <div className='w-1/2'>
                            <label
                                htmlFor='add-initial-last-name'
                                className={`w-full ${labelStyles}`}
                            >
                                Last Name
                            </label>
                            <Controller
                                name='initial_last_name'
                                control={control}
                                render={({ field }) => (
                                    <input
                                        id='add-initial-last-name'
                                        className={`w-full ${inputStyles}`}
                                        type='text'
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-initial-email'
                            className={`w-full ${labelStyles}`}
                        >
                            Email
                        </label>
                        <Controller
                            name='initial_email'
                            control={control}
                            rules={{
                                pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: 'Invalid email address',
                                },
                            }}
                            render={({ field }) => (
                                <input
                                    id='add-initial-email'
                                    className={`w-full ${inputStyles}`}
                                    type='email'
                                    {...field}
                                />
                            )}
                        />
                        {errors.initial_email && (
                            <p className={errorTextClass}>
                                {errors.initial_email.message as string}
                            </p>
                        )}
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-initial-phone'
                            className={`w-full ${labelStyles}`}
                        >
                            Contact Number
                        </label>
                        <Controller
                            name='initial_phone'
                            control={control}
                            rules={{
                                pattern: {
                                    value: /^\(\d{3}\) \d{3}-\d{4}$/,
                                    message:
                                        'Please enter a valid phone number in format (123) 456-7890',
                                },
                            }}
                            render={({ field }) => (
                                <input
                                    id='add-initial-phone'
                                    className={`w-full ${inputStyles}`}
                                    type='tel'
                                    placeholder='(123) 456-7890'
                                    maxLength={14}
                                    onChange={(e) => {
                                        // Remove all non-digits
                                        const digits = e.target.value.replace(
                                            /\D/g,
                                            '',
                                        );

                                        // Format as (XXX) XXX-XXXX
                                        let formatted = '';
                                        if (digits.length >= 1) {
                                            formatted = `(${digits.slice(
                                                0,
                                                3,
                                            )}`;
                                        }
                                        if (digits.length >= 4) {
                                            formatted += `) ${digits.slice(
                                                3,
                                                6,
                                            )}`;
                                        }
                                        if (digits.length >= 7) {
                                            formatted += `-${digits.slice(
                                                6,
                                                10,
                                            )}`;
                                        }

                                        field.onChange(formatted);
                                    }}
                                    value={field.value}
                                />
                            )}
                        />
                        {errors.initial_phone && (
                            <p className={errorTextClass}>
                                {errors.initial_phone.message as string}
                            </p>
                        )}
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-relationship'
                            className={`w-full ${labelStyles}`}
                        >
                            Relationship to Client
                        </label>
                        <Controller
                            name='campaign_is_for'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    id='add-relationship'
                                    menuPlacement='auto'
                                    className='w-full'
                                    options={relationshipOptions}
                                    isClearable
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-heard-of'
                            className={`w-full ${labelStyles}`}
                        >
                            Referred By
                        </label>
                        <Controller
                            name='heard_of_source'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    id='add-heard-of'
                                    menuPlacement='auto'
                                    className='w-full'
                                    options={heardOfOptions}
                                    isClearable
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    {isHeardOfSourceOther && (
                        <div className='mb-5'>
                            <label
                                htmlFor='add-heard-of-other'
                                className={`w-full ${labelStyles}`}
                            >
                                Please Specify
                            </label>
                            <Controller
                                name='heard_of_source_other'
                                control={control}
                                render={({ field }) => (
                                    <input
                                        id='add-heard-of-other'
                                        className={`w-full ${inputStyles}`}
                                        type='text'
                                        placeholder='How did you hear about us?'
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                    )}

                    {isMedCenterSelected && (
                        <>
                            <div className='mb-5'>
                                <label
                                    htmlFor='add-med-center-state'
                                    className={`w-full ${labelStyles}`}
                                >
                                    Where is the Medical Center located?
                                    <span className={requiredStyles}>*</span>
                                </label>
                                <Select
                                    id='add-med-center-state'
                                    menuPlacement='auto'
                                    options={medCenterStateOptions}
                                    value={selectedMedCenterState}
                                    onChange={handleMedCenterStateChange}
                                    placeholder='Select a state...'
                                    className='w-full'
                                    isDisabled={isSubmitting}
                                />
                                {showMedCenterError &&
                                    !selectedMedCenterState && (
                                        <p className={errorTextClass}>
                                            This field is required
                                        </p>
                                    )}
                            </div>

                            {selectedMedCenterState && !showAddCenterForm && (
                                <div className='mb-5'>
                                    <label
                                        htmlFor='add-med-center'
                                        className={`w-full ${labelStyles}`}
                                    >
                                        Medical Center
                                        <span className={requiredStyles}>
                                            *
                                        </span>
                                    </label>
                                    <Select
                                        id='add-med-center'
                                        menuPlacement='auto'
                                        options={medicalCenterOptions}
                                        value={selectedMedicalCenter}
                                        onChange={handleMedicalCenterChange}
                                        placeholder='Select a medical center...'
                                        className='w-full'
                                        isLoading={loadingCenters}
                                        isDisabled={
                                            isSubmitting || loadingCenters
                                        }
                                        isClearable
                                    />
                                    {showMedCenterError && (
                                        <p className={errorTextClass}>
                                            This field is required
                                        </p>
                                    )}
                                    <p className='mt-2 text-[14px] text-[#666]'>
                                        Is your Medical Center not listed here?{' '}
                                        <button
                                            type='button'
                                            className='text-[#0066CC] underline hover:text-[#0052A3]'
                                            onClick={() =>
                                                setShowAddCenterForm(true)
                                            }
                                        >
                                            Click here to add it.
                                        </button>
                                    </p>
                                </div>
                            )}

                            {isReferringSourceSelected &&
                                selectedMedicalCenter &&
                                !showAddCenterForm && (
                                    <ReferringProfessionalField
                                        id='add-referring-professional'
                                        className='mb-5'
                                        medicalCenterId={
                                            selectedMedicalCenter.value as number
                                        }
                                        value={referringProfessional}
                                        onChange={setReferringProfessional}
                                        showError={showReferrerError}
                                        disabled={isSubmitting}
                                        labelClassName={`w-full ${labelStyles}`}
                                        inputClassName={`w-full ${inputStyles}`}
                                        requiredClassName={requiredStyles}
                                    />
                                )}

                            {showAddCenterForm && (
                                <div className='mb-5 border-l-4 border-[#FEBF3E] bg-[#FEF9EC] p-4'>
                                    <h4 className='mb-3 text-[14px] font-bold'>
                                        Add New Medical Center
                                    </h4>
                                    <label
                                        htmlFor='add-new-center-name'
                                        className={`w-full ${labelStyles}`}
                                    >
                                        Medical Center Name
                                    </label>
                                    <input
                                        type='text'
                                        id='add-new-center-name'
                                        className={`w-full ${inputStyles} mb-3`}
                                        value={newCenterName}
                                        onChange={(e) =>
                                            setNewCenterName(e.target.value)
                                        }
                                        disabled={isCreatingCenter}
                                        placeholder='Enter medical center name'
                                    />
                                    <div className='flex gap-3'>
                                        <button
                                            type='button'
                                            className='rounded-full border-[2px] px-4 py-1 text-[13px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                                            onClick={() => {
                                                setShowAddCenterForm(false);
                                                setNewCenterName('');
                                            }}
                                            disabled={isCreatingCenter}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type='button'
                                            className='rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-4 py-1 text-[13px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] disabled:cursor-not-allowed disabled:opacity-50'
                                            onClick={handleAddCenter}
                                            disabled={
                                                isCreatingCenter ||
                                                !newCenterName.trim()
                                            }
                                        >
                                            {isCreatingCenter
                                                ? 'Adding...'
                                                : 'Add'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* CLIENT INFORMATION */}
                    <h2 className={sectionStyles}>CLIENT INFORMATION</h2>

                    {isCampaignForSelf && (
                        <div className='border-blue-200 bg-blue-50 text-blue-800 mb-4 rounded-lg border p-4 text-sm'>
                            <strong>Note:</strong> Client information is shared
                            from Initial Contact since the relationship is
                            "self".
                        </div>
                    )}

                    <div className='mb-5 flex gap-3'>
                        <div className='flex-1'>
                            <label
                                htmlFor='add-patient-first-name'
                                className={`w-full ${labelStyles}`}
                            >
                                First Name
                            </label>
                            <Controller
                                name='patient_first_name'
                                control={control}
                                render={({ field }) => (
                                    <input
                                        id='add-patient-first-name'
                                        className={`w-full ${inputStyles}`}
                                        type='text'
                                        disabled={isCampaignForSelf}
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                        <div className='flex-1'>
                            <label
                                htmlFor='add-patient-middle-name'
                                className={`w-full ${labelStyles}`}
                            >
                                Middle Name
                            </label>
                            <Controller
                                name='patient_middle_name'
                                control={control}
                                render={({ field }) => (
                                    <input
                                        id='add-patient-middle-name'
                                        className={`w-full ${inputStyles}`}
                                        type='text'
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                        <div className='flex-1'>
                            <label
                                htmlFor='add-patient-last-name'
                                className={`w-full ${labelStyles}`}
                            >
                                Last Name
                            </label>
                            <Controller
                                name='patient_last_name'
                                control={control}
                                render={({ field }) => (
                                    <input
                                        id='add-patient-last-name'
                                        className={`w-full ${inputStyles}`}
                                        type='text'
                                        disabled={isCampaignForSelf}
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-patient-nicknames'
                            className={`w-full ${labelStyles}`}
                        >
                            Nickname
                        </label>
                        <Controller
                            name='patient_nicknames'
                            control={control}
                            render={({ field }) => (
                                <input
                                    id='add-patient-nicknames'
                                    className={`w-full ${inputStyles}`}
                                    type='text'
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-patient-email'
                            className={`w-full ${labelStyles}`}
                        >
                            Email
                        </label>
                        <Controller
                            name='patient_email'
                            control={control}
                            rules={{
                                pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: 'Invalid email address',
                                },
                            }}
                            render={({ field }) => (
                                <input
                                    id='add-patient-email'
                                    className={`w-full ${inputStyles}`}
                                    type='email'
                                    disabled={isCampaignForSelf}
                                    {...field}
                                />
                            )}
                        />
                        {errors.patient_email && (
                            <p className={errorTextClass}>
                                {errors.patient_email.message as string}
                            </p>
                        )}
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-patient-phone'
                            className={`w-full ${labelStyles}`}
                        >
                            Contact Number
                        </label>
                        <Controller
                            name='patient_phone1'
                            control={control}
                            rules={{
                                pattern: {
                                    value: /^\(\d{3}\) \d{3}-\d{4}$/,
                                    message:
                                        'Please enter a valid phone number in format (123) 456-7890',
                                },
                            }}
                            render={({ field }) => (
                                <input
                                    id='add-patient-phone'
                                    className={`w-full ${inputStyles}`}
                                    type='tel'
                                    placeholder='(123) 456-7890'
                                    maxLength={14}
                                    disabled={isCampaignForSelf}
                                    onChange={(e) => {
                                        // Remove all non-digits
                                        const digits = e.target.value.replace(
                                            /\D/g,
                                            '',
                                        );

                                        // Format as (XXX) XXX-XXXX
                                        let formatted = '';
                                        if (digits.length >= 1) {
                                            formatted = `(${digits.slice(
                                                0,
                                                3,
                                            )}`;
                                        }
                                        if (digits.length >= 4) {
                                            formatted += `) ${digits.slice(
                                                3,
                                                6,
                                            )}`;
                                        }
                                        if (digits.length >= 7) {
                                            formatted += `-${digits.slice(
                                                6,
                                                10,
                                            )}`;
                                        }

                                        field.onChange(formatted);
                                    }}
                                    value={field.value}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-patient-address1'
                            className={`w-full ${labelStyles}`}
                        >
                            Street Address 1
                        </label>
                        <Controller
                            name='patient_address_1'
                            control={control}
                            render={({ field }) => (
                                <input
                                    id='add-patient-address1'
                                    className={`w-full ${inputStyles}`}
                                    type='text'
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-patient-address2'
                            className={`w-full ${labelStyles}`}
                        >
                            Street Address 2
                        </label>
                        <Controller
                            name='patient_address_2'
                            control={control}
                            render={({ field }) => (
                                <input
                                    id='add-patient-address2'
                                    className={`w-full ${inputStyles}`}
                                    type='text'
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-patient-city'
                            className={`w-full ${labelStyles}`}
                        >
                            City
                        </label>
                        <Controller
                            name='patient_city'
                            control={control}
                            render={({ field }) => (
                                <input
                                    id='add-patient-city'
                                    className={`w-full ${inputStyles}`}
                                    type='text'
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-patient-state'
                            className={`w-full ${labelStyles}`}
                        >
                            State
                        </label>
                        <Controller
                            name='patient_state'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    id='add-patient-state'
                                    menuPlacement='auto'
                                    className='w-full'
                                    options={stateOptions}
                                    isClearable
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-patient-zip'
                            className={`w-full ${labelStyles}`}
                        >
                            Zip Code
                        </label>
                        <Controller
                            name='patient_zip_code'
                            control={control}
                            render={({ field }) => (
                                <input
                                    id='add-patient-zip'
                                    className={`w-full ${inputStyles}`}
                                    type='text'
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-patient-birthdate'
                            className={`w-full ${labelStyles}`}
                        >
                            Birth Date
                        </label>
                        <Controller
                            name='patient_birth_date'
                            control={control}
                            render={({ field }) => (
                                <input
                                    id='add-patient-birthdate'
                                    className={`w-full ${inputStyles}`}
                                    type='date'
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label className={`w-full ${labelStyles}`}>
                            Gender
                        </label>
                        <Controller
                            name='patient_gender'
                            control={control}
                            render={({ field }) => (
                                <div className='flex flex-wrap gap-2'>
                                    {GENDER_OPTIONS.map((gender) => (
                                        <button
                                            key={gender}
                                            type='button'
                                            onClick={() =>
                                                field.onChange(
                                                    field.value === gender
                                                        ? ''
                                                        : gender,
                                                )
                                            }
                                            className={`rounded-full border-2 px-5 py-2 text-[14px]/[18px] font-bold uppercase transition-colors ${
                                                field.value === gender
                                                    ? 'border-[#2D2E33] bg-[#2D2E33] text-white'
                                                    : 'border-[#2D2E33] bg-white text-[#2D2E33] hover:bg-gray-50'
                                            }`}
                                        >
                                            {gender}
                                        </button>
                                    ))}
                                </div>
                            )}
                        />
                    </div>

                    {/* PRIMARY CONTACT */}
                    <h2 className={sectionStyles}>PRIMARY CONTACT</h2>

                    {isPrimaryContactSelf && (
                        <div className='border-blue-200 bg-blue-50 text-blue-800 mb-4 rounded-lg border p-4 text-sm'>
                            <strong>Note:</strong> Primary contact is the same
                            as the client; fields are filled from Client
                            Information.
                        </div>
                    )}

                    <div className='mb-5'>
                        <label className={`w-full ${labelStyles}`}>
                            Same as
                        </label>
                        <div className='flex gap-2'>
                            {!isCampaignForSelf && (
                                <button
                                    type='button'
                                    onClick={() =>
                                        setPrimarySameAs((prev) =>
                                            prev === 'initial_contact'
                                                ? null
                                                : 'initial_contact',
                                        )
                                    }
                                    className={`rounded-full border-2 px-5 py-2 text-[14px]/[18px] font-bold transition-colors ${
                                        primarySameAs === 'initial_contact'
                                            ? 'border-[#2D2E33] bg-[#2D2E33] text-white'
                                            : 'border-[#2D2E33] bg-white text-[#2D2E33] hover:bg-gray-50'
                                    }`}
                                >
                                    Initial Contact
                                </button>
                            )}
                            <button
                                type='button'
                                onClick={() =>
                                    setPrimarySameAs((prev) =>
                                        prev === 'client' ? null : 'client',
                                    )
                                }
                                className={`rounded-full border-2 px-5 py-2 text-[14px]/[18px] font-bold transition-colors ${
                                    primarySameAs === 'client'
                                        ? 'border-[#2D2E33] bg-[#2D2E33] text-white'
                                        : 'border-[#2D2E33] bg-white text-[#2D2E33] hover:bg-gray-50'
                                }`}
                            >
                                Client
                            </button>
                        </div>
                    </div>

                    <div className='mb-5'>
                        <Controller
                            name='primary_is_emergency_contact'
                            control={control}
                            render={({ field }) => (
                                <label className='flex cursor-pointer items-center gap-2'>
                                    <input
                                        type='checkbox'
                                        checked={field.value}
                                        onChange={(e) =>
                                            field.onChange(e.target.checked)
                                        }
                                        className='h-4 w-4 accent-[#00AEB3]'
                                    />
                                    <span className='text-[14px]/[18px]'>
                                        Make Emergency Contact
                                    </span>
                                </label>
                            )}
                        />
                    </div>

                    <div className='mb-5 flex gap-3'>
                        <div className='flex-1'>
                            <label
                                htmlFor='add-primary-first-name'
                                className={`w-full ${labelStyles}`}
                            >
                                First Name
                            </label>
                            <Controller
                                name='primary_first_name'
                                control={control}
                                render={({ field }) => (
                                    <input
                                        id='add-primary-first-name'
                                        className={`w-full ${inputStyles}`}
                                        type='text'
                                        placeholder='Enter first name'
                                        disabled={primaryFieldsLocked}
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                        <div className='flex-1'>
                            <label
                                htmlFor='add-primary-middle-name'
                                className={`w-full ${labelStyles}`}
                            >
                                Middle Name
                            </label>
                            <Controller
                                name='primary_middle_name'
                                control={control}
                                render={({ field }) => (
                                    <input
                                        id='add-primary-middle-name'
                                        className={`w-full ${inputStyles}`}
                                        type='text'
                                        placeholder='Enter middle name'
                                        disabled={primaryFieldsLocked}
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                        <div className='flex-1'>
                            <label
                                htmlFor='add-primary-last-name'
                                className={`w-full ${labelStyles}`}
                            >
                                Last Name
                            </label>
                            <Controller
                                name='primary_last_name'
                                control={control}
                                render={({ field }) => (
                                    <input
                                        id='add-primary-last-name'
                                        className={`w-full ${inputStyles}`}
                                        type='text'
                                        placeholder='Enter last name'
                                        disabled={primaryFieldsLocked}
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-primary-email'
                            className={`w-full ${labelStyles}`}
                        >
                            Email
                        </label>
                        <Controller
                            name='primary_email'
                            control={control}
                            rules={{
                                pattern: {
                                    value: /^$|^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: 'Invalid email address',
                                },
                            }}
                            render={({ field }) => (
                                <input
                                    id='add-primary-email'
                                    className={`w-full ${inputStyles}`}
                                    type='email'
                                    placeholder='email@website.com'
                                    disabled={primaryFieldsLocked}
                                    {...field}
                                />
                            )}
                        />
                        {errors.primary_email && (
                            <p className={errorTextClass}>
                                {errors.primary_email.message as string}
                            </p>
                        )}
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-primary-phone'
                            className={`w-full ${labelStyles}`}
                        >
                            Contact Number
                        </label>
                        <Controller
                            name='primary_phone'
                            control={control}
                            rules={{
                                pattern: {
                                    value: /^$|^\(\d{3}\) \d{3}-\d{4}$/,
                                    message:
                                        'Please enter a valid phone number in format (123) 456-7890',
                                },
                            }}
                            render={({ field }) => (
                                <input
                                    id='add-primary-phone'
                                    className={`w-full ${inputStyles}`}
                                    type='tel'
                                    placeholder='(123) 456-7890'
                                    maxLength={14}
                                    disabled={primaryFieldsLocked}
                                    onChange={(e) => {
                                        const digits = e.target.value.replace(
                                            /\D/g,
                                            '',
                                        );
                                        let formatted = '';
                                        if (digits.length >= 1)
                                            formatted = `(${digits.slice(
                                                0,
                                                3,
                                            )}`;
                                        if (digits.length >= 4)
                                            formatted += `) ${digits.slice(
                                                3,
                                                6,
                                            )}`;
                                        if (digits.length >= 7)
                                            formatted += `-${digits.slice(
                                                6,
                                                10,
                                            )}`;
                                        field.onChange(formatted);
                                    }}
                                    value={field.value}
                                />
                            )}
                        />
                        {errors.primary_phone && (
                            <p className={errorTextClass}>
                                {errors.primary_phone.message as string}
                            </p>
                        )}
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-primary-address1'
                            className={`w-full ${labelStyles}`}
                        >
                            Street Address 1
                        </label>
                        <Controller
                            name='primary_address_1'
                            control={control}
                            render={({ field }) => (
                                <input
                                    id='add-primary-address1'
                                    className={`w-full ${inputStyles}`}
                                    type='text'
                                    disabled={primaryFieldsLocked}
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-primary-address2'
                            className={`w-full ${labelStyles}`}
                        >
                            Street Address 2
                        </label>
                        <Controller
                            name='primary_address_2'
                            control={control}
                            render={({ field }) => (
                                <input
                                    id='add-primary-address2'
                                    className={`w-full ${inputStyles}`}
                                    type='text'
                                    disabled={primaryFieldsLocked}
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-primary-city'
                            className={`w-full ${labelStyles}`}
                        >
                            City
                        </label>
                        <Controller
                            name='primary_city'
                            control={control}
                            render={({ field }) => (
                                <input
                                    id='add-primary-city'
                                    className={`w-full ${inputStyles}`}
                                    type='text'
                                    disabled={primaryFieldsLocked}
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5 flex gap-3'>
                        <div className='flex-[3]'>
                            <label
                                htmlFor='add-primary-state'
                                className={`w-full ${labelStyles}`}
                            >
                                State
                            </label>
                            <Controller
                                name='primary_state'
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        id='add-primary-state'
                                        menuPlacement='auto'
                                        className='w-full'
                                        options={stateOptions}
                                        isClearable
                                        isDisabled={primaryFieldsLocked}
                                        placeholder='Select'
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                        <div className='flex-1'>
                            <label
                                htmlFor='add-primary-zip'
                                className={`w-full ${labelStyles}`}
                            >
                                Zip Code
                            </label>
                            <Controller
                                name='primary_zip_code'
                                control={control}
                                render={({ field }) => (
                                    <input
                                        id='add-primary-zip'
                                        className={`w-full ${inputStyles}`}
                                        type='text'
                                        placeholder='00000'
                                        maxLength={10}
                                        disabled={primaryFieldsLocked}
                                        {...field}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-primary-relationship'
                            className={`w-full ${labelStyles}`}
                        >
                            Relationship to Client
                        </label>
                        <Controller
                            name='primary_relationship_to_client'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    id='add-primary-relationship'
                                    menuPlacement='auto'
                                    className='w-full'
                                    options={relationshipOptions}
                                    isClearable
                                    isDisabled={primarySameAs !== null}
                                    placeholder='Select'
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    {/* PRIMARY MEDICAL REASON */}
                    <h2 className={sectionStyles}>PRIMARY MEDICAL REASON</h2>

                    <div className='mb-5'>
                        <label className={`w-full ${labelStyles}`}>
                            Medical Reason
                        </label>
                        <Controller
                            name='medical_reason'
                            control={control}
                            render={({ field }) => (
                                <div className='flex flex-wrap gap-2'>
                                    {medicalReasonOptions.map(
                                        (option: {
                                            value: number;
                                            label: string;
                                        }) => (
                                            <button
                                                key={option.value}
                                                type='button'
                                                onClick={() =>
                                                    field.onChange(
                                                        field.value?.value ===
                                                            option.value
                                                            ? null
                                                            : option,
                                                    )
                                                }
                                                className={`rounded-full border-2 px-5 py-2 text-[14px]/[18px] font-bold uppercase transition-colors ${
                                                    field.value?.value ===
                                                    option.value
                                                        ? 'border-[#2D2E33] bg-[#2D2E33] text-white'
                                                        : 'border-[#2D2E33] bg-white text-[#2D2E33] hover:bg-gray-50'
                                                }`}
                                            >
                                                {option.label}
                                            </button>
                                        ),
                                    )}
                                </div>
                            )}
                        />
                    </div>

                    {selectedMedicalReason &&
                        medicalIssueOptions.length > 0 && (
                            <div className='mb-5'>
                                <label
                                    htmlFor='add-medical-issue'
                                    className={`w-full ${labelStyles}`}
                                >
                                    {`Select Type of ${
                                        selectedMedicalReason.label ||
                                        'Medical Issue'
                                    }`}
                                </label>
                                <Controller
                                    name='primary_medical_issue'
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            id='add-medical-issue'
                                            menuPlacement='auto'
                                            className='w-full'
                                            options={medicalIssueOptions}
                                            isClearable
                                            {...field}
                                        />
                                    )}
                                />
                            </div>
                        )}

                    <div className='mb-5'>
                        <label
                            htmlFor='add-injury-timeframe'
                            className={`w-full ${labelStyles}`}
                        >
                            {selectedMedicalReason?.label
                                ? `${selectedMedicalReason.label} Timeframe`
                                : 'Timeframe'}
                        </label>
                        <Controller
                            name='injury_time_frame'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    id='add-injury-timeframe'
                                    menuPlacement='auto'
                                    className='w-full'
                                    options={injuryTimeFrameOptions}
                                    isClearable
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-medical-issue-date'
                            className={`w-full ${labelStyles}`}
                        >
                            {selectedMedicalReason?.label
                                ? `${selectedMedicalReason.label} Date`
                                : 'Medical Issue Date'}
                        </label>
                        <Controller
                            name='medical_issue_date'
                            control={control}
                            render={({ field }) => (
                                <input
                                    id='add-medical-issue-date'
                                    type='date'
                                    className={`w-full ${inputStyles}`}
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-medical-issue-notes'
                            className={`w-full ${labelStyles}`}
                        >
                            Notes
                        </label>
                        <Controller
                            name='medical_issue_notes'
                            control={control}
                            render={({ field }) => (
                                <textarea
                                    id='add-medical-issue-notes'
                                    className={`w-full ${inputStyles} min-h-[80px]`}
                                    rows={3}
                                    placeholder='Enter any notes about the medical issue'
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    {/* SECONDARY MEDICAL REASON */}
                    <h2 className={sectionStyles}>SECONDARY MEDICAL REASON</h2>

                    <div className='mb-5'>
                        <label className={`w-full ${labelStyles}`}>
                            Secondary Medical Reason
                        </label>
                        <div className='flex flex-wrap gap-2'>
                            {medicalReasonOptions.map(
                                (option: { value: number; label: string }) => (
                                    <button
                                        key={option.value}
                                        type='button'
                                        onClick={() => {
                                            const newReason =
                                                secondaryMedicalReason ===
                                                option.value
                                                    ? null
                                                    : option.value;
                                            setSecondaryMedicalReason(
                                                newReason,
                                            );
                                            setValue(
                                                'secondary_medical_issue',
                                                null,
                                            );
                                        }}
                                        className={`rounded-full border-2 px-5 py-2 text-[14px]/[18px] font-bold uppercase transition-colors ${
                                            secondaryMedicalReason ===
                                            option.value
                                                ? 'border-[#2D2E33] bg-[#2D2E33] text-white'
                                                : 'border-[#2D2E33] bg-white text-[#2D2E33] hover:bg-gray-50'
                                        }`}
                                    >
                                        {option.label}
                                    </button>
                                ),
                            )}
                        </div>
                    </div>

                    {secondaryMedicalReason &&
                        secondaryMedicalIssueOptions.length > 0 && (
                            <div className='mb-5'>
                                <label
                                    htmlFor='add-secondary-medical-issue'
                                    className={`w-full ${labelStyles}`}
                                >
                                    {`Select Type of ${
                                        medicalReasonOptions.find(
                                            (o: {
                                                value: number;
                                                label: string;
                                            }) =>
                                                o.value ===
                                                secondaryMedicalReason,
                                        )?.label || 'Medical Issue'
                                    }`}
                                </label>
                                <Controller
                                    name='secondary_medical_issue'
                                    control={control}
                                    render={({ field }) => (
                                        <Select
                                            id='add-secondary-medical-issue'
                                            menuPlacement='auto'
                                            className='w-full'
                                            options={
                                                secondaryMedicalIssueOptions
                                            }
                                            isClearable
                                            placeholder='Select'
                                            {...field}
                                        />
                                    )}
                                />
                            </div>
                        )}

                    {/* INSURANCE */}
                    <h2 className={sectionStyles}>INSURANCE</h2>

                    <div className='mb-5'>
                        <label
                            htmlFor='add-insurance-type'
                            className={`w-full ${labelStyles}`}
                        >
                            Insurance Type
                        </label>
                        <Controller
                            name='insurance_type'
                            control={control}
                            render={({ field }) => (
                                <Select
                                    id='add-insurance-type'
                                    menuPlacement='auto'
                                    className='w-full'
                                    options={insuranceOptions}
                                    isClearable
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    {/* COMMUNITY */}
                    <h2 className={sectionStyles}>COMMUNITY</h2>

                    <div className='mb-5'>
                        <label className={`w-full ${labelStyles}`}>
                            Community Types
                        </label>
                        <Controller
                            name='community_types'
                            control={control}
                            render={({ field }) => (
                                <div className='flex flex-col gap-2'>
                                    {communityOptions.map((ct: any) => {
                                        const isChecked = (
                                            field.value || []
                                        ).some(
                                            (v: any) => v.value === ct.value,
                                        );
                                        return (
                                            <label
                                                key={ct.value}
                                                className='flex cursor-pointer items-center gap-2 text-sm'
                                            >
                                                <input
                                                    type='checkbox'
                                                    checked={isChecked}
                                                    onChange={() => {
                                                        const current =
                                                            field.value || [];
                                                        if (isChecked) {
                                                            field.onChange(
                                                                current.filter(
                                                                    (v: any) =>
                                                                        v.value !==
                                                                        ct.value,
                                                                ),
                                                            );
                                                        } else {
                                                            field.onChange([
                                                                ...current,
                                                                ct,
                                                            ]);
                                                        }
                                                    }}
                                                />
                                                {ct.label}
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        />
                    </div>

                    {/* NOTES */}
                    <div className='mb-5'>
                        <label
                            htmlFor='add-notes'
                            className={`w-full ${labelStyles}`}
                        >
                            Notes
                        </label>
                        <Controller
                            name='notes'
                            control={control}
                            render={({ field }) => (
                                <textarea
                                    id='add-notes'
                                    className={`w-full ${inputStyles}`}
                                    rows={4}
                                    {...field}
                                />
                            )}
                        />
                    </div>

                    {contactError && (
                        <div className='mb-4 rounded-lg border border-red bg-red/10 p-4 text-sm text-red'>
                            {contactError}
                        </div>
                    )}

                    <div className='mb-[30px] flex gap-[16px]'>
                        <button
                            type='button'
                            className='mt-[30px] grow rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type='submit'
                            className='mt-[30px] grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] disabled:cursor-not-allowed disabled:opacity-50'
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Creating...' : 'Create Request'}
                        </button>
                    </div>
                </form>
            </FormProvider>
        </Drawer>
    );
};

export default AddCampaignRequestDrawer;
